import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, merge } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent
} from '@nrwl/test-setup';
import { ValuesExchangeProvider, ThresholdsService } from '@nrwl/common-lib';
import { NetworkTestService } from './network-test.service';
import { NetworkTestComponent } from './components/network-test/network-test.component';
import { takeUntil } from 'rxjs/operators';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'network-test',
  providers: [NetworkTestService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class NetworkTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  setupId: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: NetworkTestService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  editMode: boolean;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  currentObj: any; //eslint-disable-line
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    router: Router,
    setupService: NetworkTestService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly thresholdService: ThresholdsService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      const defaultParams = merge({}, this.dataId, JSON.parse(get(data, 'defaultParams', '{}')));
      this.setupService.openTestSetup(this.setupId, this.setupConfig, defaultParams);
    });
    this.exchange.notifyListeners('new_monitor', null);
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.setupService.onNext();
    this.editMode = get(this.setupService.defaultParamsById, 'editMode', false);
    this.createNetworkTest();
  }
  createNetworkTest(): void {
    const requestObject = this.setupService.getRequestObject();
    const message = this.editMode ? 'Updating test' : 'Creating test';
    this.setupService.openTestStatusDialog(message, this.editMode ? 'Update' : 'Add');
    const responseProcessed = this.setupService._saveRequest(requestObject, false);
    responseProcessed.subscribe(response => {
      this.setupService.output.monitorid = response.success.id;
      if (this.setupService.requestFailed) {
        this.setupService.updateBulkTestStatusDialog(
          this.editMode ? 'Failed to update network test' : 'Failed to create network test',
          this.setupService.errorDescription,
          false
        );
      } else {
        this.createNetworkThreshold();
      }
      this.exchange.notifyListeners('new_monitor', response);
    });
  }

  createNetworkThreshold(): void {
    this.setupService.output.siteId = this.editMode
      ? this.setupService.output.originatorSite
      : this.setupService.output.originator.site.id;
    const requestObject = this.setupService.getThresholdRequestObject();
    if (requestObject && requestObject.length) {
      let responseObject = null;
      if (this.editMode && !this.setupService.output.noThresholdExists) {
        responseObject = this.thresholdService.updateThreshold$(requestObject);
      } else {
        responseObject = this.thresholdService.createThreshold$(requestObject);
      }
      responseObject.pipe(takeUntil(this.unsubscribe)).subscribe(
        () => {
          this.closeSetupPage();
        },
        () => {
          this.closeSetupPage();
        }
      );
    } else {
      this.closeSetupPage();
    }
  }

  closeSetupPage(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
    this.setupService.updateBulkTestStatusDialog(
      this.editMode ? 'Network test has been updated' : 'Network test has been created',
      '',
      true
    );
  }

  getSetupService(): NetworkTestService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getFactoryResolver(): ComponentFactoryResolver {
    return this.factoryResolver;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'network-test.component.html':
        return NetworkTestComponent;

      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
